import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CaretRightIcon,
  ChatCircleDotsIcon,
  CheckIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';

import { Button } from '@/ui/button/Button';
import { Textarea } from '@/ui/Textarea';
import classNames from '@/utils/classnames';

import type { PendingAsk } from '../../stores/agent-session-store';
import {
  type AskAnswer,
  type AskDraft,
  type AskQuestion,
  buildAnswers,
  deferDraft,
  emptyDraft,
  initialDrafts,
  isAnswered,
  toggleOption,
  toggleWriting,
} from '../../stores/ask-user';

/**
 * Long enough to see the choice register, short enough not to feel like waiting. Only ever used
 * moving *forward* into a question that has not been answered yet.
 */
const ADVANCE_DELAY_MS = 240;

/**
 * The question the agent stopped on, asked directly above the composer.
 *
 * One question at a time, sliding. Showing all of them at once turned a two-click decision into a
 * wall of radio buttons — and with four questions of five options the card was taller than the
 * conversation it was about. Answering advances automatically, but every question stays reachable
 * from the dots and the Back button, so nothing is committed until the last screen.
 *
 * Focus is deliberately not stolen — a user mid-sentence in the composer would lose their words —
 * so the card announces itself politely instead and the composer's placeholder changes.
 */
export function AskUserCard({
  ask,
  disabled = false,
  onSubmit,
}: {
  ask: PendingAsk;
  /** The session can't be reached yet, so an answer has nowhere to go. */
  disabled?: boolean;
  /** Resolves false when the answer couldn't be delivered, so the drafts are kept. */
  onSubmit: (answers: AskAnswer[]) => Promise<boolean>;
}) {
  const [drafts, setDrafts] = useState<AskDraft[]>(() => initialDrafts(ask.questions));
  const [index, setIndex] = useState(0);
  const [sending, setSending] = useState(false);

  const total = ask.questions.length;
  const question = ask.questions[index]!;
  const draft = drafts[index] ?? emptyDraft();
  const last = index === total - 1;
  const busy = sending || disabled;
  const unanswered = drafts.filter((d) => !isAnswered(d)).length;

  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelAdvance = () => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = null;
  };
  useEffect(() => cancelAdvance, []);

  function goTo(next: number) {
    cancelAdvance();
    setIndex(Math.min(Math.max(next, 0), total - 1));
  }

  function setDraft(next: AskDraft, { advance = false } = {}) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? next : d)));
    // Only ever forward, and only into a question the user has not answered yet: auto-advancing
    // on a *changed* answer would bounce them straight back out of the question they came back to
    // fix, which is the one thing this must not do.
    if (advance && !last) {
      cancelAdvance();
      advanceTimer.current = setTimeout(() => goTo(index + 1), ADVANCE_DELAY_MS);
    }
  }

  function pick(option: string) {
    const wasAnswered = isAnswered(draft);
    // Multi-select cannot advance on a pick — there is no way to tell a first choice from a final
    // one — so it waits for Next.
    setDraft(toggleOption(draft, option, question.multiSelect), {
      advance: !question.multiSelect && !wasAnswered,
    });
  }

  async function submit() {
    if (busy) return;
    cancelAdvance();
    setSending(true);
    // Stays `sending` on success: answering unmounts this card (the tool call gains a result), so
    // re-enabling the form would flash a live question that has already been settled.
    if (!(await onSubmit(buildAnswers(ask.questions, drafts)))) setSending(false);
  }

  // Every slide stays mounted so the track can animate between them, which means the viewport has
  // to be told how tall the *current* one is or it would size itself to the longest question.
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [height, setHeight] = useState<number>();
  useLayoutEffect(() => {
    const el = slideRefs.current[index];
    if (el) setHeight(el.offsetHeight);
  }, [index, drafts]);

  return (
    <section
      data-ask-user-card
      aria-label={total > 1 ? `${total} questions from the agent` : 'A question from the agent'}
      className="flex shrink-0 flex-col gap-3 border-t border-line-subtle bg-warning-tint px-6 py-3.5"
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
          e.preventDefault();
          if (last) void submit();
          else goTo(index + 1);
        }
        // Escape hands the keyboard to the composer without dismissing anything — the question is
        // still pending, and answering it in prose is a legitimate reply.
        if (e.key === 'Escape') document.querySelector<HTMLElement>('[data-composer-input]')?.focus();
      }}
    >
      {/* Polite: the card is already visible, and the user may be mid-sentence below it. */}
      <div aria-live="polite" className="sr-only">
        {total > 1 ? `The agent asked ${total} questions.` : 'The agent asked a question.'}
      </div>

      <div className="flex items-center gap-2">
        <ChatCircleDotsIcon size={13} weight="fill" className="shrink-0 text-warning" />
        <span className="min-w-0 truncate text-2xs font-semibold tracking-[0.06em] text-warning uppercase">
          {question.header}
        </span>
        {total > 1 && (
          <>
            <span className="shrink-0 text-2xs tabular-nums text-fg-tertiary">
              {index + 1} of {total}
            </span>
            <div className="ml-auto flex shrink-0 items-center gap-1">
              {ask.questions.map((q, i) => (
                <button
                  key={`${q.header}-${i}`}
                  type="button"
                  disabled={busy}
                  onClick={() => goTo(i)}
                  aria-label={`Question ${i + 1}: ${q.header}`}
                  aria-current={i === index}
                  className={classNames(
                    'h-2 rounded-full transition-all focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    i === index
                      ? 'w-4 bg-warning'
                      : isAnswered(drafts[i]!)
                        ? 'w-2 bg-warning/50'
                        : 'w-2 bg-fg-tertiary/25',
                  )}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div
        className="overflow-hidden transition-[height] duration-200 motion-reduce:transition-none"
        style={{ height }}
      >
        <div
          className="flex transition-transform duration-200 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {ask.questions.map((q, i) => (
            <div
              key={`${q.header}-${i}`}
              ref={(el) => {
                slideRefs.current[i] = el;
              }}
              // Off-screen slides must not be tabbable, or the keyboard walks into questions that
              // are not showing.
              inert={i !== index}
              className="w-full shrink-0 self-start"
            >
              <QuestionSlide
                question={q}
                draft={drafts[i] ?? emptyDraft()}
                disabled={busy}
                onPick={pick}
                onToggleWriting={() => setDraft(toggleWriting(draft))}
                onText={(text) => setDraft({ ...draft, text })}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {index > 0 && (
          <Button
            variant="ghost"
            size={8}
            disabled={busy}
            onClick={() => goTo(index - 1)}
            iconLeft={<ArrowLeftIcon size={13} />}
          >
            Back
          </Button>
        )}
        {/* Per question, not per card: which decisions actually matter is exactly what varies
            between them, and one card-wide skip made the cheap way out the only way out. */}
        <Button variant="ghost" size={8} disabled={busy} onClick={() => setDraft(deferDraft(), { advance: true })}>
          {draft.deferred ? 'Agent will decide this' : 'Let the agent decide'}
        </Button>

        <div className="ml-auto">
          {last ? (
            <Button variant="primary" size={8} isLoading={sending} disabled={busy} onClick={() => void submit()}>
              {total > 1 ? 'Send answers' : 'Send answer'}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size={8}
              disabled={busy}
              onClick={() => goTo(index + 1)}
              iconRight={<ArrowRightIcon size={13} />}
            >
              Next
            </Button>
          )}
        </div>
      </div>

      {last && unanswered > 0 && (
        <p className="text-2xs text-fg-tertiary">
          {unanswered === total
            ? 'Nothing answered — the agent will decide all of it.'
            : `${unanswered} unanswered — the agent will decide ${unanswered === 1 ? 'that one' : 'those'}.`}
        </p>
      )}
    </section>
  );
}

function QuestionSlide({
  question,
  draft,
  disabled,
  onPick,
  onToggleWriting,
  onText,
}: {
  question: AskQuestion;
  draft: AskDraft;
  disabled: boolean;
  onPick: (option: string) => void;
  onToggleWriting: () => void;
  onText: (text: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 pr-px">
      {/* The question itself, first — it is what the user is here to read. */}
      <p className="text-sm leading-snug font-medium text-fg">{question.question}</p>
      {question.multiSelect && <p className="-mt-1 text-2xs text-fg-tertiary">Pick as many as apply.</p>}

      <div
        role={question.multiSelect ? 'group' : 'radiogroup'}
        aria-label={question.question}
        className="flex max-h-56 flex-col gap-1 overflow-y-auto"
      >
        {question.options.map((option) => (
          <OptionRow
            key={option}
            label={option}
            multiSelect={question.multiSelect}
            checked={draft.selected.includes(option)}
            disabled={disabled}
            onClick={() => onPick(option)}
          />
        ))}
        {/* Free text is always on offer, whatever the agent asked. Rendered as a peer of the
            options and mutually exclusive with them, so there is exactly one escape hatch — the
            agent is told not to write its own, and any it writes anyway is dropped server-side. */}
        <OptionRow
          label="Write your own answer"
          icon={<PencilSimpleIcon size={12} className="mt-px shrink-0" />}
          multiSelect={false}
          checked={draft.writing}
          disabled={disabled}
          onClick={onToggleWriting}
          muted
        />
      </div>

      {draft.writing && (
        <Textarea
          autoFocus
          rows={2}
          value={draft.text}
          disabled={disabled}
          placeholder="In your own words…"
          onChange={(e) => onText(e.target.value)}
        />
      )}
    </div>
  );
}

/**
 * One option as a full-width row with an explicit control on the left.
 *
 * A square that fills with a tick for multi-select, a circle for single-select. The pill-only
 * version gave no hint that several answers were allowed, so a "pick all that apply" question read
 * as "pick one" — and full-width rows also give long option labels somewhere to go.
 */
function OptionRow({
  label,
  icon,
  multiSelect,
  checked,
  disabled,
  onClick,
  muted = false,
}: {
  label: string;
  icon?: React.ReactNode;
  multiSelect: boolean;
  checked: boolean;
  disabled: boolean;
  onClick: () => void;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      role={multiSelect ? 'checkbox' : 'radio'}
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className={classNames(
        'flex w-full items-start gap-2 rounded-lg border px-2.5 py-1.5 text-left text-xs transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50',
        checked
          ? 'border-brand-border bg-brand-tint font-medium text-brand-text'
          : muted
            ? 'border-line border-dashed bg-surface-2 text-fg-tertiary hover:text-fg-secondary'
            : 'border-line bg-surface-2 text-fg-secondary hover:border-line-strong hover:text-fg',
      )}
    >
      <span
        aria-hidden
        className={classNames(
          'mt-px flex size-3.5 shrink-0 items-center justify-center border transition-colors',
          multiSelect ? 'rounded-sm' : 'rounded-full',
          checked ? 'border-brand bg-brand text-white' : 'border-line-strong',
        )}
      >
        {checked && <CheckIcon size={9} weight="bold" />}
      </span>
      {icon}
      {/* Never truncated: an option the user cannot read is an option they cannot choose. */}
      <span className="min-w-0 break-words">{label}</span>
      {muted && !checked && <CaretRightIcon size={11} className="mt-0.5 ml-auto shrink-0 opacity-50" />}
    </button>
  );
}
